import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Rss, Zap, Youtube, Key } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VideoSourceSettingsProps {
  currentSource: string;
  onSourceChange: (source: string) => void;
  hasApiKey: boolean;
}

const VideoSourceSettings = ({ currentSource, onSourceChange, hasApiKey }: VideoSourceSettingsProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Source des vidéos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={currentSource} onValueChange={onSourceChange}>
          <div className="flex items-center space-x-3 p-3 border rounded-lg">
            <RadioGroupItem value="rss" id="rss" />
            <Label htmlFor="rss" className="flex-1 cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Rss className="h-4 w-4 text-orange-500" />
                  <span className="font-medium">Flux RSS (Recommandé)</span>
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    Par défaut
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Utilise les flux RSS publics des chaînes YouTube. Gratuit et illimité, mais données limitées (pas de vues ni durée).
              </p>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 border rounded-lg">
            <RadioGroupItem value="youtube_api" id="youtube_api" disabled={!hasApiKey} />
            <Label htmlFor="youtube_api" className={`flex-1 ${!hasApiKey ? 'opacity-50' : 'cursor-pointer'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Youtube className="h-4 w-4 text-red-500" />
                  <span className="font-medium">YouTube API officielle</span>
                  <Badge variant="outline" className="text-red-600 border-red-300">
                    Clé requise
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                API officielle YouTube. Données complètes mais limitée par quotas quotidiens.
              </p>
            </Label>
          </div>
        </RadioGroup>

        {!hasApiKey && (
          <Alert>
            <Key className="h-4 w-4" />
            <AlertDescription>
              Pour utiliser l'API YouTube officielle, configurez d'abord votre clé API dans l'onglet "Clé API YouTube".
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-muted-foreground">
          <strong>Note :</strong> Le système utilisera RSS comme fallback automatique si la source sélectionnée échoue.
        </div>
      </CardContent>
    </Card>
  );
};

export default VideoSourceSettings;